/**
 * Utility functions for supplier display
 */

/** Supplier-like object that may have businessName or user with companyName/fullName */
interface SupplierDisplaySource {
  businessName?: string | null;
  serviceDescription?: string | null;
  user?: {
    companyName?: string | null;
    fullName?: string | null;
  } | null;
}

const BREADCRUMB_MAX_LENGTH = 40;

/**
 * Gets a user-friendly display name for a supplier.
 * Uses businessName, user.companyName, user.fullName, or truncated serviceDescription.
 */
export function getSupplierDisplayName(
  supplier: SupplierDisplaySource | null | undefined,
  fallback = 'Supplier'
): string {
  if (!supplier) return fallback;
  const name =
    supplier.businessName || supplier.user?.companyName || supplier.user?.fullName || null;
  if (name && name.trim()) return name.trim();
  if (supplier.serviceDescription?.trim()) {
    const desc = supplier.serviceDescription.trim();
    return desc.length > BREADCRUMB_MAX_LENGTH
      ? `${desc.slice(0, BREADCRUMB_MAX_LENGTH - 3)}...`
      : desc;
  }
  return fallback;
}

/**
 * Gets a truncated display name suitable for breadcrumbs (avoids overflow).
 */
export function getSupplierBreadcrumbLabel(
  supplier: SupplierDisplaySource | null | undefined,
  fallback = 'Supplier'
): string {
  const name = getSupplierDisplayName(supplier, fallback);
  if (name.length <= BREADCRUMB_MAX_LENGTH) return name;
  return `${name.slice(0, BREADCRUMB_MAX_LENGTH - 3)}...`;
}
